/**
 * Format elapsed milliseconds into hours, minutes, seconds
 * @param elapsedMs - Elapsed time in milliseconds
 * @returns Formatted time object with display strings
 */
export function formatElapsedTime(elapsedMs) {
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    // Full display: "0:00:00" or "1:23:45"
    const display = hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes}:${seconds.toString().padStart(2, '0')}`;
    // Short display for compact mode: "1h 23m" or "23m" or "45s"
    let displayShort;
    if (hours > 0) {
        displayShort = `${hours}h ${minutes}m`;
    }
    else if (minutes > 0) {
        displayShort = `${minutes}m`;
    }
    else {
        displayShort = `${seconds}s`;
    }
    return {
        hours,
        minutes,
        seconds,
        display,
        displayShort,
    };
}
/**
 * Get accessibility label for screen readers
 */
export function getAccessibilityLabel(isClockedIn, isLoading, formattedTime) {
    if (isLoading) {
        return 'Loading time tracking status';
    }
    if (isClockedIn) {
        const { hours, minutes } = formattedTime;
        if (hours > 0) {
            return `Checked in for ${hours} hours and ${minutes} minutes. Tap to check out.`;
        }
        return `Checked in for ${minutes} minutes. Tap to check out.`;
    }
    return 'Not checked in. Tap to check in.';
}
/**
 * Design tokens for the WorkSessionClock component
 * These align with the Turn Connect design system
 */
export const clockTokens = {
    colors: {
        // Active/clocked in state
        active: {
            background: '#2F5F3F', // Green tint for active
            backgroundHover: '#3A7A4F',
            border: '#4CAF50',
            text: '#4CAF50',
            icon: '#4CAF50',
        },
        // On break state (amber/yellow)
        onBreak: {
            background: '#3F3F2F', // Amber tint for on break
            backgroundHover: '#4A4A3F',
            border: '#F59E0B',
            text: '#F59E0B',
            icon: '#F59E0B',
        },
        // Inactive/not clocked in state
        inactive: {
            background: '#1A1A1A',
            backgroundHover: '#2A2A2A',
            border: '#333333',
            text: '#CCCCCC',
            icon: '#CCCCCC',
        },
        // Loading state
        loading: {
            background: '#1A1A1A',
            border: '#444444',
            text: '#666666',
            icon: '#666666',
        },
        // Disabled state
        disabled: {
            background: '#0F0F0F',
            border: '#222222',
            text: '#444444',
            icon: '#444444',
        },
        // Error state
        error: {
            background: '#3F2F2F',
            border: '#DC2626',
            text: '#DC2626',
            icon: '#DC2626',
        },
    },
    /**
     * The same states on a light ground. `colors` above stays the dark set so
     * every existing caller is unaffected; a themed host passes scheme="light"
     * and gets these. Backgrounds are tints rather than saturated fills so the
     * chip does not read as a coloured blob in a light header.
     */
    colorsLight: {
        active: {
            background: 'rgba(21, 128, 61, 0.12)',
            backgroundHover: 'rgba(21, 128, 61, 0.18)',
            border: '#15803D',
            text: '#15803D',
            icon: '#15803D',
        },
        onBreak: {
            background: 'rgba(180, 83, 9, 0.12)',
            backgroundHover: 'rgba(180, 83, 9, 0.18)',
            border: '#B45309',
            text: '#B45309',
            icon: '#B45309',
        },
        inactive: {
            background: '#ECECED',
            backgroundHover: '#E3E3E5',
            border: '#D6D6DA',
            text: '#3F3F46',
            icon: '#3F3F46',
        },
        loading: {
            background: '#ECECED',
            border: '#D6D6DA',
            text: '#A1A1AA',
            icon: '#A1A1AA',
        },
        disabled: {
            background: '#F4F4F5',
            border: '#E4E4E7',
            text: '#A1A1AA',
            icon: '#A1A1AA',
        },
        error: {
            background: 'rgba(185, 28, 28, 0.10)',
            border: '#B91C1C',
            text: '#B91C1C',
            icon: '#B91C1C',
        },
    },
    sizes: {
        compact: {
            height: 32,
            padding: 8,
            borderRadius: 16,
            iconSize: 14,
            fontSize: 12,
            gap: 4,
        },
        normal: {
            height: 40,
            padding: 12,
            borderRadius: 20,
            iconSize: 16,
            fontSize: 13,
            gap: 8,
        },
    },
    animation: {
        duration: '150ms',
        easing: 'ease-out',
    },
};
/**
 * Operator-facing message for a missing location, keyed by WHY it is
 * missing. Each reason needs a different action, so each gets different
 * words — telling someone whose browser has location hard-blocked to
 * "enable location services" sends them to the wrong place, and telling
 * someone with a flaky GPS fix to change browser settings wastes their
 * time when "try again" would have worked.
 */
export function describeLocationFailure(reason) {
    switch (reason) {
        case 'permission_denied':
            return "Location access is blocked for this site, and this location requires it to check in. Allow location in your browser's site settings (the icon next to the address bar), then try again.";
        case 'unsupported':
            return 'This browser cannot provide your location, which this location requires to check in. Try a different browser or device.';
        case 'timeout':
            return 'Getting your location timed out, and this location requires it to check in. Check for an unanswered location prompt in your browser, then try again — or move somewhere with a better signal.';
        case 'unavailable':
            return 'Your device could not determine your location, which is required to check in here. Try again, or move somewhere with a better signal.';
        default:
            // No specific reason (e.g. the user dismissed the permission prompt).
            return 'Location is required to check in at this location. Allow the location prompt and try again.';
    }
}
/** Longest error the clock's popover renders before truncating. */
const CLOCK_ERROR_MAX_LENGTH = 220;
/**
 * Normalize an error string for the clock's compact error popover.
 *
 * The hook's error channel can carry raw server payloads — JSON bodies
 * ('{"error":"...","code":"..."}'), "HTTP 500: <html>…" transport strings —
 * which read as garbage in a 280px popover and can be arbitrarily long.
 * Pulls the human field out of JSON-looking strings, rewrites bare
 * transport failures, and truncates the rest. Never returns an empty
 * string for a non-empty input.
 */
export function sanitizeClockErrorMessage(raw, maxLength = CLOCK_ERROR_MAX_LENGTH) {
    let message = (raw || '').trim();
    // JSON-looking payloads: prefer the conventional human-readable fields.
    if (message.startsWith('{')) {
        try {
            const parsed = JSON.parse(message);
            const candidate = parsed.error ?? parsed.message;
            if (typeof candidate === 'string' && candidate.trim()) {
                message = candidate.trim();
            }
        }
        catch {
            // Not valid JSON after all — fall through to plain handling.
        }
    }
    // Bare transport/status noise the user can't act on verbatim.
    if (/^(HTTP\s*5\d\d\b|Internal Server Error$)/i.test(message)) {
        message = 'The server had a problem processing this. Try again in a moment.';
    }
    else if (/^(failed to fetch|network(\s?error| request failed)?|load failed)$/i.test(message)) {
        message = 'Could not reach the server. Check your connection and try again.';
    }
    if (!message) {
        message = 'Something went wrong. Try again.';
    }
    if (message.length > maxLength) {
        message = `${message.slice(0, maxLength - 1).trimEnd()}…`;
    }
    return message;
}
/**
 * The clock-in time of an already-running session, as a person reads it.
 * Returns null when the server gave us nothing usable — better to say only
 * where than to print "Invalid Date".
 */
export function formatCheckInTime(startTime) {
    if (!startTime)
        return null;
    const at = new Date(startTime);
    if (Number.isNaN(at.getTime()))
        return null;
    return at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
/**
 * What to tell someone whose check-in is already running when they tap check
 * in again.
 *
 * About TIME, never place. One check-in covers the whole day and follows
 * them between the account's locations, so where they happened to punch in
 * is no longer anything they need to act on — and naming it invites exactly
 * the question this change answers with "no": should I move? Their hours are
 * theirs for the period, not a shop's.
 */
export function describeOpenCheckIn(startTime) {
    const at = formatCheckInTime(startTime);
    return at ? `You're already checked in — your time has been running since ${at}.` : "You're already checked in and your time is running.";
}
/** The reassurance under it: nothing to do, nothing to move. */
export const OPEN_CHECK_IN_HINT = 'One check-in covers your whole day, wherever you work. Carry on — there is nothing to do here.';
